import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import Stepper from './Stepper';
import CreateCampaignStep1 from './campaign/CreateCampaignStep1';
import CreateCampaignStep2 from './campaign/CreateCampaignStep2';
import CreateCampaignStep3 from './campaign/CreateCampaignStep3';
import CreateCampaignStep4 from './campaign/CreateCampaignStep4';
import CreateCampaignStep5 from './campaign/CreateCampaignStep5';
import { FormDataForCreateCampaign } from '@/types/interfaces';
import { useNavigate, useLocation, useBlocker } from 'react-router-dom';
import { Campaign } from '@/interfaces/campaign';
import { getPlatformsByType } from '@/utils/socialplatformUtils';
import useI18n from '@/hooks/useI18n';
import {
  createBrandCampaign,
  updateBrandCampaign,
} from '@/services/userService';
import { useBrandTotalsQuery } from '@/hooks/queries/useBrandQueries';
import { UserContext } from '@/context/UserContext';
import PreviewCampaign from '@/pages/PreviewCampaign';
import { CampaignStatusEnum } from '@/types/enums';
import SaveAsDraftModal from '../Modal/SaveAsDraftModal';
import { IUser } from '@/interfaces/User';
import { formatMoney } from '@/utils/numberUtils';

interface CampaignDetailComponentProps {
  campaign?: Campaign;
  isEditCampaign?: boolean;
  onStepChange?: (step: number) => void;
}

interface TotalType {
  totalDeposit: number;
  totalSpent: number;
  remainingBudget: number;
  depositCount: number;
  campaignCount: number;
}

const MultiStepForm: React.FC<CampaignDetailComponentProps> = ({
  campaign,
  isEditCampaign,
  onStepChange,
}) => {
  const { t } = useI18n();
  const [openModal, setOpenModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const totalSteps = 6;
  const navigate = useNavigate();
  const location = useLocation();
  const platforms = getPlatformsByType(campaign?.containType || {});
  const [isLoading, setIsLoading] = useState(false);
  const [shouldBlockNavigation, setShouldBlockNavigation] = useState(true);

  const userContext = useContext(UserContext);

  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { user, refreshCampaignStats, setUser } = userContext;

  const { data: brandTotalsData } = useBrandTotalsQuery(user?.brand?._id ?? '');
  const brandTotals: TotalType = brandTotalsData ?? {
    totalDeposit: 0,
    totalSpent: 0,
    remainingBudget: 0,
    depositCount: 0,
    campaignCount: 0,
  };

  const initialFormData = useRef<FormDataForCreateCampaign>({
    brandLogo: campaign?.brandImageUrl || '',
    brandName: campaign?.brandName || '',
    startDate: campaign?.startDate || '',
    endDate: campaign?.endDate || undefined,
    brandDescription: campaign?.brandDescription || '',
    budget: campaign?.campaignBudget || undefined,
    smartLink: campaign?.smartLink || '',
    validFor: campaign?.validFor || [],
    limitPerPerson: campaign?.limitPerPerson || 0,
    conditions: campaign?.campaignTermsAndConditions || [],
    reelCashbackRange: [
      campaign?.containType?.reel?.cashBackRangeFrom ?? undefined,
      campaign?.containType?.reel?.cashBackRangeTo ?? undefined,
    ],
    reelMaxCashback:
      campaign?.containType?.reel?.maximumCashBackPerCreator || null,
    reelPlatforms: platforms.reel || [],
    postCashbackRange: [
      campaign?.containType?.post?.cashBackRangeFrom ?? undefined,
      campaign?.containType?.post?.cashBackRangeTo ?? undefined,
    ],
    postMaxCashback:
      campaign?.containType?.post?.maximumCashBackPerCreator || null,
    postPlatforms: platforms.post || [],
    storyCashbackRange: [
      campaign?.containType?.story?.cashBackRangeFrom ?? undefined,
      campaign?.containType?.story?.cashBackRangeTo ?? undefined,
    ],
    storyMaxCashback:
      campaign?.containType?.story?.maximumCashBackPerCreator || null,
    storyPlatforms: platforms.story || [],
    reachArea: campaign?.storeAreaLocation.type ?? 'country',
    selectedItems: campaign?.storeAreaLocation.list || [],
    contentDescription: campaign?.contentDescription || '',
    contentTitle: campaign?.contentTitle || '',
    hashtags: campaign?.hashTags || [],
    instagramTags: campaign?.instagramTags?.length
      ? campaign.instagramTags
      : campaign?.mentions || [],
    tiktokTags: campaign?.tiktokTags?.length
      ? campaign.tiktokTags
      : campaign?.mentions || [],
    runCampaignTillMoneyEnd: campaign?.runCampaignTillMoneyEnd || false,
    status: campaign?.status || 'scheduled',
    ...(isEditCampaign && { smartLinkId: campaign?.smartLinkId }),
  });

  const [formData, setFormData] = useState<FormDataForCreateCampaign>(
    initialFormData.current,
  );

  const [nextLocation, setNextLocation] = useState<null | { pathname: string }>(
    null,
  );
  const memberRole =
    user?.brand?.members?.find((member) => member.userId === user._id)?.role ||
    '';

  const isUserRole = memberRole === 'user';
  useEffect(() => {
    if (isUserRole) {
      navigate('/active-campaign', { replace: true });
    }
  }, [isUserRole]);

  // useEffect(() => {
  //   const isDirty =
  //     JSON.stringify(formData) !== JSON.stringify(initialFormData.current);
  //   setIsFormDirty(isDirty);
  // }, [formData]);

  useEffect(() => {
    if (isFormDirty && !isEditCampaign) {
      window.onbeforeunload = () =>
        'You have unsaved changes. Are you sure you want to leave?';
      return () => {
        window.onbeforeunload = null;
      };
    }
  }, [isFormDirty, isEditCampaign]);
  const resolveSubmitStatus = (
    currentStatus: CampaignStatusEnum,
    startDate?: string,
  ) => {
    if (currentStatus === CampaignStatusEnum.Draft && startDate) {
      const start = new Date(startDate);
      const now = new Date();

      if (start > now) return CampaignStatusEnum.Scheduled;
      return CampaignStatusEnum.Live;
    }
    if (!startDate) {
      return CampaignStatusEnum.Scheduled;
    }

    const now = new Date();
    const start = new Date(startDate);

    return now >= start
      ? CampaignStatusEnum.Live
      : CampaignStatusEnum.Scheduled;
  };

  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }) => {
        if (
          shouldBlockNavigation &&
          isFormDirty &&
          currentLocation.pathname.includes('create-campaign') &&
          !currentLocation.pathname.includes('edit-campaign') &&
          currentLocation.pathname !== nextLocation.pathname
        ) {
          setOpenModal(true);
          setNextLocation(nextLocation);
          return true;
        }
        return false;
      },
      [isFormDirty, shouldBlockNavigation],
    ),
  );

  const handleStay = () => {
    setOpenModal(false);
    if (nextLocation) {
      blocker?.proceed();
      navigate(nextLocation.pathname);
    }
  };

  const handleLeave = async () => {
    setOpenModal(false);

    if (!formData.brandName || formData.brandName.trim() === '') {
      toast.error(t('common.brandNameRequired'));
      return;
    }

    const reelPlatforms =
      formData?.reelPlatforms &&
      formData.reelPlatforms.includes('instagram') &&
      formData.reelPlatforms.includes('tiktok')
        ? 'both'
        : formData?.reelPlatforms
          ? formData.reelPlatforms[0]
          : undefined;
    const postPlatforms =
      formData?.postPlatforms &&
      formData.postPlatforms.includes('instagram') &&
      formData.postPlatforms.includes('tiktok')
        ? 'both'
        : formData?.postPlatforms
          ? formData.postPlatforms[0]
          : undefined;
    const storyPlatforms =
      formData?.storyPlatforms &&
      formData.storyPlatforms.includes('instagram') &&
      formData.storyPlatforms.includes('tiktok')
        ? 'both'
        : formData?.storyPlatforms
          ? formData.storyPlatforms[0]
          : undefined;
    const draftFormData = {
      brandName: formData.brandName ?? undefined,
      brandId: user?.brand._id || '',
      brandImageUrl: formData.brandLogo ?? '',
      startDate: formData.startDate ?? '',
      endDate: formData.endDate ?? undefined,
      brandDescription: formData?.brandDescription ?? '',
      runCampaignTillMoneyEnd: formData.runCampaignTillMoneyEnd ?? false,
      campaignBudget: formData.budget ?? 0,
      validFor: (formData.validFor as ('store' | 'online' | 'both')[]) ?? [
        'online',
      ],
      smartLink: formData.smartLink ?? '',
      limitPerPerson: formData.limitPerPerson ?? 0,
      campaignTermsAndConditions: formData.conditions ?? [],
      containType: {
        reel: Object.values({
          cashBackRangeFrom:
            (formData.reelCashbackRange ?? [0])[0] ?? undefined,
          cashBackRangeTo: (formData.reelCashbackRange ?? [0])[1] ?? undefined,
          maximumCashBackPerCreator: formData.reelMaxCashback ?? undefined,
          platform: reelPlatforms ?? undefined,
        }).some((val) => val !== undefined)
          ? {
              cashBackRangeFrom:
                (formData.reelCashbackRange ?? [0])[0] ?? undefined,
              cashBackRangeTo:
                (formData.reelCashbackRange ?? [0])[1] ?? undefined,
              maximumCashBackPerCreator: formData.reelMaxCashback ?? undefined,
              platform: reelPlatforms ?? undefined,
            }
          : undefined,
        post: Object.values({
          cashBackRangeFrom:
            (formData.postCashbackRange ?? [0])[0] ?? undefined,
          cashBackRangeTo: (formData.postCashbackRange ?? [0])[1] ?? undefined,
          maximumCashBackPerCreator: formData.postMaxCashback ?? undefined,
          platform: postPlatforms ?? undefined,
        }).some((val) => val !== undefined)
          ? {
              cashBackRangeFrom:
                (formData.postCashbackRange ?? [0])[0] ?? undefined,
              cashBackRangeTo:
                (formData.postCashbackRange ?? [0])[1] ?? undefined,
              maximumCashBackPerCreator: formData.postMaxCashback ?? undefined,
              platform: postPlatforms ?? undefined,
            }
          : undefined,
        story: Object.values({
          cashBackRangeFrom:
            (formData.storyCashbackRange ?? [0])[0] ?? undefined,
          cashBackRangeTo: (formData.storyCashbackRange ?? [0])[1] ?? undefined,
          maximumCashBackPerCreator: formData.storyMaxCashback ?? undefined,
          platform: storyPlatforms ?? undefined,
        }).some((val) => val !== undefined)
          ? {
              cashBackRangeFrom:
                (formData.storyCashbackRange ?? [0])[0] ?? undefined,
              cashBackRangeTo:
                (formData.storyCashbackRange ?? [0])[1] ?? undefined,
              maximumCashBackPerCreator: formData.storyMaxCashback ?? undefined,
              platform: storyPlatforms ?? undefined,
            }
          : undefined,
      },
      storeAreaLocation: {
        type: formData.reachArea ? formData.reachArea : 'global',
        list: formData.selectedItems || [],
      },
      contentTitle: formData.contentTitle ?? '',
      hashTags: formData.hashtags ?? [],
      instagramTags: formData.instagramTags ?? [],
      tiktokTags: formData.tiktokTags ?? [],
      contentDescription: formData.contentDescription ?? '',
      status: CampaignStatusEnum.Draft,
    };

    try {
      await createBrandCampaign(draftFormData);
      toast.success(String(t('campaign.messages.campaignSavedAsDraft')));
      setIsFormDirty(false);
      await refreshCampaignStats();
      blocker.proceed();
      navigate('/active-campaign', {
        state: { tab: 'draft' },
      });
    } catch (error) {
      toast.error(t('common.savingDraft'));
    }
  };

  const [imagePreview, setImagePreview] = useState<string | null>(
    formData?.brandLogo || null,
  );

  const handleChange = (
    newValues: Partial<FormDataForCreateCampaign>,
    options?: { skipDirty?: boolean },
  ) => {
    setShouldBlockNavigation(true);
    setFormData((prev) => ({ ...prev, ...newValues }));
    if (!options?.skipDirty) {
      setIsFormDirty(true);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isFormDirty && !isEditCampaign && !openModal) {
        e.preventDefault();
        e.returnValue = ''; // Trigger browser's default confirmation dialog
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isFormDirty, openModal, isEditCampaign]);

  // const handleSubmit = async (values: Partial<FormDataForCreateCampaign>) => {
  //   const reelPlatforms =
  //     values?.reelPlatforms &&
  //     values.reelPlatforms.includes('instagram') &&
  //     values.reelPlatforms.includes('tiktok')
  //       ? 'both'
  //       : values?.reelPlatforms
  //         ? values.reelPlatforms[0]
  //         : undefined;
  //   const postPlatforms =
  //     values?.postPlatforms &&
  //     values.postPlatforms.includes('instagram') &&
  //     values.postPlatforms.includes('tiktok')
  //       ? 'both'
  //       : values?.postPlatforms
  //         ? values.postPlatforms[0]
  //         : undefined;
  //   const storyPlatforms =
  //     values?.storyPlatforms &&
  //     values.storyPlatforms.includes('instagram') &&
  //     values.storyPlatforms.includes('tiktok')
  //       ? 'both'
  //       : values?.storyPlatforms
  //         ? values.storyPlatforms[0]
  //         : undefined;
  //   const resolvedStatus = isEditCampaign
  //     ? getEffectiveStatus(
  //         campaign?.status ?? CampaignStatusEnum.Draft,
  //         values.startDate,
  //       )
  //     : resolveSubmitStatus(
  //         campaign?.status ?? CampaignStatusEnum.Draft,
  //         values.startDate,
  //       );

  //   const formData = {
  //     brandName: values.brandName ?? undefined,
  //     brandId: user?.brand._id || '',
  //     brandImageUrl: values.brandLogo ?? '',
  //     startDate: values.startDate ?? '',
  //     endDate: values.endDate ?? undefined,
  //     status: resolvedStatus,
  //     brandDescription: values?.brandDescription ?? '',
  //     runCampaignTillMoneyEnd: values.runCampaignTillMoneyEnd ?? false,
  //     campaignBudget: values.budget ?? 0,
  //     validFor: (values.validFor as ('store' | 'online' | 'both')[]) ?? [
  //       'online',
  //     ],
  //     smartLink: values.smartLink ?? '',
  //     limitPerPerson: values.limitPerPerson ?? 0,
  //     campaignTermsAndConditions: values.conditions ?? [],
  //     containType: {
  //       reel: Object.values({
  //         cashBackRangeFrom: (values.reelCashbackRange ?? [0])[0] ?? undefined,
  //         cashBackRangeTo: (values.reelCashbackRange ?? [0])[1] ?? undefined,
  //         maximumCashBackPerCreator: values.reelMaxCashback ?? undefined,
  //         platform: reelPlatforms ?? undefined,
  //       }).some((val) => val !== undefined)
  //         ? {
  //             cashBackRangeFrom:
  //               (values.reelCashbackRange ?? [0])[0] ?? undefined,
  //             cashBackRangeTo:
  //               (values.reelCashbackRange ?? [0])[1] ?? undefined,
  //             maximumCashBackPerCreator: values.reelMaxCashback ?? undefined,
  //             platform: reelPlatforms ?? undefined,
  //           }
  //         : undefined,
  //       post: Object.values({
  //         cashBackRangeFrom: (values.postCashbackRange ?? [0])[0] ?? undefined,
  //         cashBackRangeTo: (values.postCashbackRange ?? [0])[1] ?? undefined,
  //         maximumCashBackPerCreator: values.postMaxCashback ?? undefined,
  //         platform: postPlatforms ?? undefined,
  //       }).some((val) => val !== undefined)
  //         ? {
  //             cashBackRangeFrom:
  //               (values.postCashbackRange ?? [0])[0] ?? undefined,
  //             cashBackRangeTo:
  //               (values.postCashbackRange ?? [0])[1] ?? undefined,
  //             maximumCashBackPerCreator: values.postMaxCashback ?? undefined,
  //             platform: postPlatforms ?? undefined,
  //           }
  //         : undefined,
  //       story: Object.values({
  //         cashBackRangeFrom: (values.storyCashbackRange ?? [0])[0] ?? undefined,
  //         cashBackRangeTo: (values.storyCashbackRange ?? [0])[1] ?? undefined,
  //         maximumCashBackPerCreator: values.storyMaxCashback ?? undefined,
  //         platform: storyPlatforms ?? undefined,
  //       }).some((val) => val !== undefined)
  //         ? {
  //             cashBackRangeFrom:
  //               (values.storyCashbackRange ?? [0])[0] ?? undefined,
  //             cashBackRangeTo:
  //               (values.storyCashbackRange ?? [0])[1] ?? undefined,
  //             maximumCashBackPerCreator: values.storyMaxCashback ?? undefined,
  //             platform: storyPlatforms ?? undefined,
  //           }
  //         : undefined,
  //     },
  //     storeAreaLocation: {
  //       type: values.reachArea ? values.reachArea : 'global',
  //       list: values.selectedItems || [],
  //     },
  //     contentTitle: values.contentTitle ?? '',
  //     hashTags: values.hashtags ?? [],
  //     mentions: values.tags ?? [],
  //     contentDescription: values.contentDescription ?? '',
  //     ...(isEditCampaign && { smartLinkId: values.smartLinkId }),
  //   };

  //   try {
  //     setIsLoading(true);
  //     setShouldBlockNavigation(false);
  //     if (isEditCampaign && campaign?._id) {
  //       await editCampaign(campaign._id, formData);
  //     } else {
  //       await createCampaign(formData);
  //     }
  //     setIsFormDirty(false);
  //   } catch (error) {
  //     toast.error(t('wallet.errors.spending'));
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };
  const handleSubmit = async (values: Partial<FormDataForCreateCampaign>) => {
    // Helper to determine platform string
    const getPlatform = (platforms?: string[]) => {
      if (!platforms || platforms.length === 0) return undefined;
      if (platforms.includes('instagram') && platforms.includes('tiktok'))
        return 'both';
      return platforms[0];
    };

    // Helper to create containType entries only if they have meaningful data
    const getContainType = (
      range?: number[] | null,
      max?: number | null,
      platforms?: string[] | null,
    ) => {
      const from = range?.[0];
      const to = range?.[1];

      const hasValidRange =
        typeof from === 'number' &&
        typeof to === 'number' &&
        !(from === 0 && to === 0);

      const hasValidMax = typeof max === 'number';
      const hasPlatform = Array.isArray(platforms) && platforms.length > 0;

      // ❌ If nothing meaningful exists → DON'T RETURN OBJECT
      if (!hasValidRange && !hasValidMax && !hasPlatform) {
        return undefined;
      }

      return {
        ...(hasValidRange && {
          cashBackRangeFrom: from,
          cashBackRangeTo: to,
        }),
        ...(hasValidMax && {
          maximumCashBackPerCreator: max,
        }),
        ...(hasPlatform && {
          platform:
            platforms!.includes('instagram') && platforms!.includes('tiktok')
              ? 'both'
              : platforms![0],
        }),
      };
    };

    // Determine resolved status
    const resolvedStatus = isEditCampaign
      ? getEffectiveStatus(
          campaign?.status ?? CampaignStatusEnum.Draft,
          values.startDate,
        )
      : resolveSubmitStatus(
          campaign?.status ?? CampaignStatusEnum.Draft,
          values.startDate,
        );

    // Build containType only with keys that have values
    const containType: Record<string, any> = {};

    const reel = getContainType(
      values.reelCashbackRange,
      values.reelMaxCashback,
      values.reelPlatforms,
    );

    const post = getContainType(
      values.postCashbackRange,
      values.postMaxCashback,
      values.postPlatforms,
    );

    const story = getContainType(
      values.storyCashbackRange,
      values.storyMaxCashback,
      values.storyPlatforms,
    );

    if (reel) containType.reel = reel;
    if (post) containType.post = post;
    if (story) containType.story = story;

    // Build final formData
    const formData = {
      brandName: values.brandName ?? undefined,
      brandId: user?.brand._id || '',
      brandImageUrl: values.brandLogo ?? '',
      startDate: values.startDate ?? '',
      endDate: values.endDate ?? undefined,
      status: resolvedStatus,
      brandDescription: values.brandDescription ?? '',
      runCampaignTillMoneyEnd: values.runCampaignTillMoneyEnd ?? false,
      campaignBudget: values.budget ?? 0,
      validFor: (values.validFor as ('store' | 'online' | 'both')[]) ?? [
        'online',
      ],
      smartLink: values.smartLink ?? '',
      limitPerPerson: values.limitPerPerson ?? 0,
      campaignTermsAndConditions: values.conditions ?? [],
      containType: containType, // only keys with data included
      storeAreaLocation: {
        type: values.reachArea ?? 'global',
        list: values.selectedItems || [],
      },
      contentTitle: values.contentTitle ?? '',
      hashTags: values.hashtags ?? [],
      instagramTags: values.instagramTags ?? [],
      tiktokTags: values.tiktokTags ?? [],
      contentDescription: values.contentDescription ?? '',
      ...(isEditCampaign && { smartLinkId: values.smartLinkId }),
    };

    console.log('formData', formData);
    try {
      setIsLoading(true);
      setShouldBlockNavigation(false);
      if (isEditCampaign && campaign?._id) {
        await editCampaign(campaign._id, formData);
      } else {
        await createCampaign(formData);
      }
      setIsFormDirty(false);
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message;
      toast.error(msg || t('wallet.errors.spending'));
    } finally {
      setIsLoading(false);
    }
  };

  const createCampaign = async (CampaignData: Campaign) => {
    try {
      const response = await createBrandCampaign(CampaignData);
      if (response) {
        toast.success(
          String(t('campaign.messages.campaignCreatedSuccessfully')),
        );

        setUser((prev: IUser) => ({
          ...prev,
          brand: {
            ...prev?.brand,
            brandLogo: CampaignData.brandImageUrl,
          },
        }));

        await refreshCampaignStats();
        if (response.status === CampaignStatusEnum.Scheduled) {
          navigate('/scheduled-campaign');
        } else if (response.status === CampaignStatusEnum.Live) {
          navigate('/active-campaign');
        }
      }
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message;
      toast.error(msg || t('wallet.errors.spending'));
    }
  };
  const getEffectiveStatus = (
    _backendStatus: CampaignStatusEnum,
    startDate?: string,
  ) => {
    if (!startDate) return _backendStatus;

    const start = new Date(startDate);
    const now = new Date();
    if (start <= now) {
      return CampaignStatusEnum.Live;
    }
    return CampaignStatusEnum.Scheduled;
  };

  const editCampaign = async (campaignId: string, updateData: Campaign) => {
    try {
      const response = await updateBrandCampaign(campaignId, updateData);
      if (!response) return;
      toast.success(String(t('campaign.messages.campaignUpdatedSuccessfully')));
      await refreshCampaignStats();
      const effectiveStatus = getEffectiveStatus(
        response.status,
        updateData.startDate,
      );
      if (effectiveStatus === CampaignStatusEnum.Live) {
        navigate('/active-campaign', { replace: true });
        return;
      }
      if (effectiveStatus === CampaignStatusEnum.Scheduled) {
        navigate('/scheduled-campaign', { replace: true });
        return;
      }
      navigate('/active-campaign', { replace: true });
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message;
      toast.error(msg || t('wallet.errors.spending'));
    }
  };

  useEffect(() => {
    onStepChange?.(currentStep);
  }, []);

  const handleNext = () => {
    if (currentStep < totalSteps) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      onStepChange?.(nextStep);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      onStepChange?.(prevStep);
    }
  };

  const handleStepClick = (step: number) => {
    setCurrentStep(step);
    onStepChange?.(step);
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setNextLocation(null);
  };

  const renderForm = () => {
    switch (currentStep) {
      case 1:
        return (
          <CreateCampaignStep1
            formData={formData}
            handleChange={handleChange}
            imagePreview={imagePreview}
            setImagePreview={setImagePreview}
            onNext={handleNext}
          />
        );
      case 2:
        return (
          <CreateCampaignStep2
            formData={formData}
            handleChange={handleChange}
            onNext={handleNext}
            onPrevious={handlePrevious}
            availableBalance={brandTotals.remainingBudget}
            originalBudget={
              isEditCampaign && campaign?.status !== 'draft'
                ? (campaign?.campaignBudget ?? 0)
                : 0
            }
          />
        );
      case 3:
        return (
          <CreateCampaignStep3
            formData={formData}
            handleChange={handleChange}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 4:
        return (
          <CreateCampaignStep4
            formData={formData}
            handleChange={handleChange}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 5:
        return (
          <CreateCampaignStep5
            formData={formData}
            handleChange={handleChange}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 6:
        return (
          <PreviewCampaign
            isEditCampaign={isEditCampaign ?? false}
            formData={formData}
            onNext={(formData) => {
              handleSubmit(formData);
            }}
            isLoading={isLoading}
            onPrevious={handlePrevious}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {currentStep !== 6 && (
        <div className="bg-[#FAFAFA] p-[16px] rounded-2xl flex justify-between items-center shrink-0">
          <Stepper
            currentStep={currentStep}
            totalSteps={totalSteps}
            onStepClick={handleStepClick}
          />
          {currentStep === 2 && (
            <div className="bg-[#2F1869] p-[24px] rounded-3xl">
              <h3 className="text-[14px] text-[#FFFFFF] font-normal font-content">
                {t('wallet.walletHeader.availableBalance')}
              </h3>
              <h1 className="text-2xl text-[#FFFFFF] font-bold font-header">
                {formatMoney(brandTotals.remainingBudget ?? 0)}
              </h1>
            </div>
          )}
        </div>
      )}
      <div
        className={`flex flex-col flex-1 min-h-0 ${
          currentStep !== 6 ? 'mt-6' : ''
        }`}
      >
        {renderForm()}
      </div>
      <SaveAsDraftModal
        isOpen={openModal}
        onClose={handleModalClose}
        handleStay={handleStay}
        handleLeave={handleLeave}
      />
    </div>
  );
};

export default MultiStepForm;
